import React from 'react';
import { Helmet } from 'react-helmet-async';

const MetaTags = ({ 
  title, 
  description, 
  keywords, 
  ogTitle, 
  ogDescription, 
  ogImage, 
  ogUrl, 
  twitterCard = 'summary_large_image',
  twitterTitle,
  twitterDescription,
  twitterImage,
  canonicalUrl,
  noIndex = false,
  noFollow = false,
  // JobPosting specific props
  jobPosting = null,
  // BlogPosting specific props
  blogPosting = null,
  // WebPage specific props for CMS pages
  webPage = null
}) => {
  const siteName = 'JobSeekers Page';
  const baseUrl = window.location.origin;
  const defaultImage = `${baseUrl}/assets/images/jobseekers-logo.png`;
  
  // Default meta tags for JobSeekers Page
  const defaultKeywords = 'job search, employment, career, hiring, recruitment, job portal, job seekers, employers, job opportunities, resume builder, job applications';
  const defaultDescription = 'JobSeekers Page - Your trusted partner for job search and recruitment. Find the perfect job or hire the best talent with our comprehensive job portal featuring advanced search, resume builder, and applicant tracking system.';

  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title ? `${title} | ${siteName}` : siteName}</title>
      <meta name="description" content={description || defaultDescription} />
      <meta name="keywords" content={keywords || defaultKeywords} />
      <meta name="author" content={siteName} />
      <meta name="robots" content={noIndex ? 'noindex' : 'index'} />
      <meta name="robots" content={noFollow ? 'nofollow' : 'follow'} />
      
      {/* Canonical URL */}
      {canonicalUrl && <link rel="canonical" href={canonicalUrl} />}
      
      {/* Language and Character Set */}
      <meta charSet="utf-8" />
      <meta httpEquiv="Content-Language" content="en" />
      <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
      
      {/* Open Graph Meta Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={ogTitle || title || siteName} />
      <meta property="og:description" content={ogDescription || description || defaultDescription} />
      <meta property="og:image" content={ogImage || defaultImage} />
      <meta property="og:url" content={ogUrl || window.location.href} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      
      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:site" content="@jobseekerspage" />
      <meta name="twitter:creator" content="@jobseekerspage" />
      <meta name="twitter:title" content={twitterTitle || ogTitle || title || siteName} />
      <meta name="twitter:description" content={twitterDescription || ogDescription || description || defaultDescription} />
      <meta name="twitter:image" content={twitterImage || ogImage || defaultImage} />
      
      {/* Additional Meta Tags */}
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#6f42c1" />
      <meta name="msapplication-TileColor" content="#6f42c1" />
      <meta name="application-name" content={siteName} />
      <meta name="apple-mobile-web-app-title" content={siteName} />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      
      {/* Search Engine Optimization */}
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      <meta name="slurp" content="index, follow" />
      
      {/* Additional SEO Meta Tags */}
      <meta name="revisit-after" content="7 days" />
      <meta name="rating" content="general" />
      <meta name="distribution" content="global" />
      <meta name="coverage" content="worldwide" />
      <meta name="target" content="all" />
      <meta name="HandheldFriendly" content="true" />
      <meta name="format-detection" content="telephone=no" />
      
      {/* Structured Data for JobSeekers Page */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebSite",
          "name": siteName,
          "url": baseUrl,
          "description": defaultDescription,
          "potentialAction": {
            "@type": "SearchAction",
            "target": `${baseUrl}/jobs?q={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        })}
      </script>
      
      {/* Organization Schema */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Organization",
          "name": siteName,
          "url": baseUrl,
          "logo": defaultImage,
          "description": defaultDescription,
          "sameAs": [
            "https://www.facebook.com/jobseekerspagedotcom",
            "https://www.linkedin.com/company/jobseekerspage-com/posts/",
            "https://www.instagram.com/jobseekerspagedotcom/"
          ]
        })}
      </script>
      
      {/* JobPosting Schema - Only render if jobPosting data is provided */}
      {jobPosting && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "JobPosting",
            "title": jobPosting.title,
            "description": jobPosting.description,
            "datePosted": jobPosting.datePosted,
            "validThrough": jobPosting.validThrough,
            "employmentType": jobPosting.employmentType,
            "hiringOrganization": {
              "@type": "Organization",
              "name": jobPosting.hiringOrganization?.name || "JobSeekers Page",
              "url": jobPosting.hiringOrganization?.url || baseUrl,
              "logo": jobPosting.hiringOrganization?.logo || defaultImage
            },
            "jobLocation": {
              "@type": "Place",
              "address": {
                "@type": "PostalAddress",
                "addressLocality": jobPosting.jobLocation?.addressLocality || "Remote",
                "addressRegion": jobPosting.jobLocation?.addressRegion || "US",
                "addressCountry": jobPosting.jobLocation?.addressCountry || "US"
              }
            },
            "applicantLocationRequirements": {
              "@type": "Country",
              "name": jobPosting.applicantLocationRequirements?.name || "United States"
            },
            "baseSalary": {
              "@type": "MonetaryAmount",
              "currency": jobPosting.baseSalary?.currency || "USD",
              "value": {
                "@type": "QuantitativeValue",
                "minValue": jobPosting.baseSalary?.minValue || 0,
                "maxValue": jobPosting.baseSalary?.maxValue || 0,
                "unitText": jobPosting.baseSalary?.unitText || "YEAR"
              }
            },
            "jobLocationType": jobPosting.jobLocationType || "TELECOMMUTE",
            "url": jobPosting.url || window.location.href,
            // Additional fields for better SEO
            ...(jobPosting.qualifications && { "qualifications": jobPosting.qualifications }),
            ...(jobPosting.experienceRequirements && { 
              "experienceRequirements": {
                "@type": "OccupationalExperienceRequirements",
                "monthsOfExperience": parseInt(jobPosting.experienceRequirements.minExperience) || 1
              }
            }),
            ...(jobPosting.numberOfVacancies && { "numberOfVacancies": jobPosting.numberOfVacancies }),
            ...(jobPosting.skills && { "skills": jobPosting.skills }),
            ...(jobPosting.keywords && { "keywords": jobPosting.keywords })
          })}
        </script>
      )}

      {/* BlogPosting Schema - Only render if blogPosting data is provided */}
      {blogPosting && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blogPosting.title,
            "description": blogPosting.description,
            "datePublished": blogPosting.datePublished,
            "dateModified": blogPosting.dateModified,
            "author": {
              "@type": "Organization",
              "name": blogPosting.author?.name || "JobSeekers Page",
              "url": blogPosting.author?.url || baseUrl
            },
            "publisher": {
              "@type": "Organization",
              "name": blogPosting.publisher?.name || "JobSeekers Page",
              "url": blogPosting.publisher?.url || baseUrl,
              "logo": {
                "@type": "ImageObject",
                "url": blogPosting.publisher?.logo?.url || defaultImage
              }
            },
            "image": {
              "@type": "ImageObject",
              "url": blogPosting.image
            },
            "url": blogPosting.url,
            "keywords": blogPosting.keywords,
            "articleSection": blogPosting.articleSection || "Career Advice",
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": blogPosting.mainEntityOfPage?.url || blogPosting.url
            }
          })}
        </script>
      )}

      {/* WebPage Schema for CMS pages - Only render if webPage data is provided */}
      {webPage && (
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            "name": webPage.title,
            "description": webPage.description,
            "datePublished": webPage.datePublished,
            "dateModified": webPage.dateModified,
            "author": {
              "@type": "Organization",
              "name": webPage.author?.name || "JobSeekers Page",
              "url": webPage.author?.url || baseUrl
            },
            "publisher": {
              "@type": "Organization",
              "name": webPage.publisher?.name || "JobSeekers Page",
              "url": webPage.publisher?.url || baseUrl,
              "logo": {
                "@type": "ImageObject",
                "url": webPage.publisher?.logo?.url || defaultImage
              }
            },
            "url": webPage.url,
            "keywords": webPage.keywords,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": webPage.mainEntityOfPage?.url || webPage.url
            },
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {
                  "@type": "ListItem",
                  "position": 1,
                  "name": "Home",
                  "item": baseUrl
                },
                {
                  "@type": "ListItem",
                  "position": 2,
                  "name": webPage.breadcrumb?.name || webPage.title,
                  "item": webPage.breadcrumb?.url || webPage.url
                }
              ]
            }
          })}
        </script>
      )}
    </Helmet>
  );
};

export default MetaTags; 